import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Spinner, XCircle, ArrowRight } from '@phosphor-icons/react';
import axios from 'axios';
import { API_URL } from '../config/api';

export const SubscriptionSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [pollCount, setPollCount] = useState(0);
  const maxPolls = 10;
  const pollInterval = 2000; // 2 seconds

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const checkStatus = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/api/payments/subscription-status/${sessionId}`,
          { withCredentials: true }
        );

        if (response.data.status === 'complete' && response.data.payment_status === 'paid') {
          setStatus('success');
          // Refresh user data to get updated subscription
          if (refreshUser) {
            await refreshUser();
          }
          return true;
        } else if (response.data.status === 'expired') {
          setStatus('error');
          return true;
        }
        return false;
      } catch (error) {
        console.error('Error checking subscription status:', error);
        // Keep polling unless we've hit max
        return false;
      }
    };

    const poll = async () => {
      const isDone = await checkStatus();
      if (!isDone && pollCount < maxPolls) {
        setTimeout(() => {
          setPollCount(prev => prev + 1);
        }, pollInterval);
      } else if (!isDone) {
        // Max polls reached without success
        setStatus('error');
      }
    };

    poll();
  }, [sessionId, pollCount, refreshUser]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {status === 'checking' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Spinner size={48} className="animate-spin text-green-600" />
              </div>
              <CardTitle className="text-xl">Processing Your Subscription...</CardTitle>
            </>
          )}
          
          {status === 'success' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                <CheckCircle size={48} weight="fill" className="text-green-600" />
              </div>
              <CardTitle className="text-xl text-green-600">Welcome to TurfPulse Premium!</CardTitle>
            </>
          )}
          
          {status === 'error' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <XCircle size={48} weight="fill" className="text-red-600" />
              </div>
              <CardTitle className="text-xl text-red-600">Something Went Wrong</CardTitle>
            </>
          )}
        </CardHeader>
        
        <CardContent className="text-center space-y-4">
          {status === 'checking' && (
            <p className="text-muted-foreground">
              Please wait while we confirm your payment...
            </p>
          )}
          
          {status === 'success' && (
            <>
              <p className="text-muted-foreground">
                Your subscription is now active. You have full access to all TurfPulse features.
              </p>
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 text-left">
                <h4 className="font-semibold text-green-800 dark:text-green-200 mb-2">Your subscription includes:</h4>
                <ul className="text-sm text-green-700 dark:text-green-300 space-y-1">
                  <li>• All 4 GDD Departments (PGR, Weed, Pest, Disease)</li>
                  <li>• Soil Analysis & 5-Day Spray Forecast</li>
                  <li>• Unlimited zones & properties</li>
                  <li>• Priority support</li>
                  <li>• Auto-renewal for uninterrupted access</li>
                </ul>
              </div>
              <Button 
                onClick={() => navigate('/app')} 
                className="w-full bg-green-600 hover:bg-green-700"
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </>
          )}
          
          {status === 'error' && (
            <>
              <p className="text-muted-foreground">
                We couldn't confirm your subscription. If you were charged, your subscription will be activated shortly.
              </p>
              <div className="flex flex-col gap-2">
                <Button 
                  onClick={() => navigate('/profile')} 
                  className="w-full"
                  data-testid="check-subscription-btn"
                >
                  Check Subscription Status
                </Button>
                <Button 
                  onClick={() => navigate('/app')} 
                  variant="outline"
                  className="w-full"
                >
                  Return to Dashboard
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Need help? Contact support@getturfpulse.com
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionSuccessPage;
